import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api';

type TrialModule = {
  module_code: string;
  module_name: string;
  trial_start_date?: string;
  trial_end_date: string;
  auto_convert_to_paid: boolean;
  is_trial?: boolean;
};

type PortalLicense = {
  portal_is_trial: boolean;
  portal_trial_start_date: string | null;
  portal_trial_end_date: string | null;
  portal_auto_convert_to_paid: boolean;
  portal_valid_from: string | null;
  portal_valid_until: string | null;
  is_trial_expired: boolean;
  is_locked_out: boolean;
  organization_name?: string;
  organization_short_code?: string;
};

// Mapping zwischen Routen und Modul-Codes
const ROUTE_TO_MODULE: { [key: string]: string } = {
  '/dienstplan': 'dienstplan',
  '/dienstplan-verwaltung': 'dienstplan',
  '/sonderdienste': 'dienstplan',
  '/meldewesen': 'meldewesen',
  '/poolwaesche': 'poolwaesche',
  '/statistik': 'statistik',
  '/fahrzeugverwaltung': 'fahrzeugverwaltung',
  '/vereinskasse': 'vereinskasse',
  '/cashbox': 'vereinskasse',
  '/messages': 'messages',
  '/nachrichten': 'messages',
  '/bonuspunkte': 'bonuspunkte',
  '/admin/bonuspunkte': 'bonuspunkte',
  '/bonuspunkte/admin': 'bonuspunkte',
  '/trust-network': 'trust_network',
  '/license-control': 'license_control',
  '/license-scan': 'license_control',
  '/expense-claims': 'expense_claims',
  '/my-expense-requests': 'expense_claims',
  '/pos': 'pos',
  '/pos-dashboard': 'pos',
  '/lager': 'lager',
  '/storage': 'storage',
  '/balance': 'balance',
  '/orgapilot-crm': 'orgapilot_crm',
  '/weapons-inventory': 'weapons_inventory',
};

interface TrialModuleBannerProps {
  children?: React.ReactNode;
}

export default function TrialModuleBanner({ children }: TrialModuleBannerProps) {
  const [trialModules, setTrialModules] = useState<TrialModule[]>([]);
  const [portalLicense, setPortalLicense] = useState<PortalLicense | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Support Request Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<TrialModule | null>(null);
  const [requestType, setRequestType] = useState<'extend' | 'buy'>('extend');
  const [requestedDuration, setRequestedDuration] = useState('14 Tage');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    loadTrialModules();
  }, []);

  async function loadTrialModules() {
    try {
      const [data, pLic] = await Promise.all([
        apiFetch('/admin/my-trial-modules').catch(() => []),
        apiFetch('/admin/my-portal-license').catch(() => null)
      ]);
      setTrialModules(data || []);
      setPortalLicense(pLic || null);
    } catch (error) {
      console.error('Fehler beim Laden der Testzeitraum-Module:', error);
      setTrialModules([]);
    } finally {
      setLoading(false);
    }
  }

  const openRequestModal = (module: TrialModule, type: 'extend' | 'buy') => {
    setSelectedModule(module);
    setRequestType(type);
    setRequestedDuration(type === 'extend' ? '14 Tage' : '12 Monate');
    setContactName(localStorage.getItem('user') || '');
    setContactEmail('');
    setContactPhone('');
    setMessage('');
    setSubmitSuccess(null);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedModule) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const res = await apiFetch('/admin/trial-modules/request-extension', {
        method: 'POST',
        body: JSON.stringify({
          module_code: selectedModule.module_code,
          request_type: requestType,
          requested_duration: requestedDuration,
          contact_name: contactName,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          message: message,
        }),
      });

      setSubmitSuccess(res.message || 'Ihre Anfrage wurde erfolgreich als Ticket beim Vertrieb eingereicht.');
    } catch (err: any) {
      setSubmitError(err?.message || 'Fehler beim Übermitteln der Anfrage. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  // Ermittle den Modul-Code für die aktuelle Route
  const getCurrentModuleCode = (): string | null => {
    const path = location.pathname;
    
    // Direkte Übereinstimmung
    if (ROUTE_TO_MODULE[path]) {
      return ROUTE_TO_MODULE[path];
    }
    
    // Überprüfe, ob der Pfad mit einem bekannten Modul-Pfad beginnt
    for (const [routePrefix, moduleCode] of Object.entries(ROUTE_TO_MODULE)) {
      if (path.startsWith(routePrefix + '/') || path.startsWith(routePrefix)) {
        return moduleCode;
      }
    }
    
    return null;
  };

  if (loading) {
    return <>{children}</>;
  }

  const isPortalLocked = !!(portalLicense && portalLicense.is_locked_out && !location.pathname.startsWith('/admin'));

  const currentModuleCode = getCurrentModuleCode();
  if (!currentModuleCode && !isPortalLocked) {
    return <>{children}</>; // Keine Modul-spezifische Seite und kein Portal-Lockout
  }

  // Filtere nur das Modul für die aktuelle Seite
  const currentModuleTrials = currentModuleCode 
    ? trialModules.filter(module => module.module_code === currentModuleCode)
    : [];

  if (currentModuleTrials.length === 0 && !isPortalLocked) {
    return <>{children}</>; // Aktuelles Modul ist nicht im Testzeitraum und kein Portal-Lockout
  }

  const now = new Date();
  const threeDays = 3 * 24 * 60 * 60 * 1000;

  // Prüfe, ob das Modul abgelaufen ist und nach Ablauf gesperrt werden soll
  const activeModuleTrial = currentModuleTrials[0];
  const endDate = activeModuleTrial ? new Date(activeModuleTrial.trial_end_date) : new Date();
  const timeLeft = endDate.getTime() - now.getTime();
  const isExpired = timeLeft < 0;
  const isLockedOut = isExpired && !activeModuleTrial?.auto_convert_to_paid;

  // --- RENDERING: MODAL FÜR SUPPORT-ANFRAGE AN DEN VERTRIEB ---
  const renderSupportModal = () => {
    if (!isModalOpen || !selectedModule) return null;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-xl w-full p-6 sm:p-8 border border-gray-200 dark:border-gray-700 space-y-5 animate-in fade-in zoom-in-95 duration-200">
          
          <div className="flex items-start justify-between border-b pb-4 dark:border-gray-700">
            <div className="space-y-1">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300">
                Support-Ticket an den Vertrieb
              </span>
              <h3 className="text-xl font-bold text-gray-900 dark:text-whitetext-white flex items-center gap-2">
                <span>🎫</span>
                <span>Anfrage für Modul: {selectedModule.module_name}</span>
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                Modul-Code: {selectedModule.module_code}
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:bg-gray-700hover:bg-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 font-bold transition"
            >
              ✕
            </button>
          </div>

          {submitSuccess ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-3xl mx-auto shadow-inner">
                ✓
              </div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-whitetext-white">Ticket erfolgreich übermittelt!</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                {submitSuccess}
              </p>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium">
                Das Vertriebs-Team wurde benachrichtigt und wird Ihre Freischaltung oder Verlängerung schnellstmöglich bearbeiten.
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSubmitSuccess(null);
                  }}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow transition"
                >
                  Schließen
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSendRequest} className="space-y-4">
              {submitError && (
                <div className="p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-xl text-xs text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
                  <span>⚠️</span>
                  <span>{submitError}</span>
                </div>
              )}

              {/* Anfragetyp: Verlängern vs Kaufen */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">Was möchten Sie anfragen?</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className={`p-3 rounded-xl border text-xs font-semibold cursor-pointer flex items-start gap-2.5 transition ${
                    requestType === 'extend'
                      ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 shadow-sm'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}>
                    <input
                      type="radio"
                      name="modal_req_type"
                      checked={requestType === 'extend'}
                      onChange={() => {
                        setRequestType('extend');
                        setRequestedDuration('14 Tage');
                      }}
                      className="mt-0.5 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-bold">⏱️ Testphase verlängern</div>
                      <div className="text-[11px] opacity-75 font-normal">Kostenfreie Verlängerung des Testzeitraums anfragen</div>
                    </div>
                  </label>

                  <label className={`p-3 rounded-xl border text-xs font-semibold cursor-pointer flex items-start gap-2.5 transition ${
                    requestType === 'buy'
                      ? 'bg-purple-50 dark:bg-purple-950/40 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200 shadow-sm'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}>
                    <input
                      type="radio"
                      name="modal_req_type"
                      checked={requestType === 'buy'}
                      onChange={() => {
                        setRequestType('buy');
                        setRequestedDuration('12 Monate');
                      }}
                      className="mt-0.5 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <div className="font-bold">💳 Modul kaufen / freischalten</div>
                      <div className="text-[11px] opacity-75 font-normal">Kostenpflichtige Freischaltung als Regulär-Abo</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Gewünschte Laufzeit */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                  {requestType === 'extend' ? 'Gewünschte Test-Verlängerung:' : 'Gewünschte Abo-Laufzeit:'}
                </label>
                <select
                  value={requestedDuration}
                  onChange={(e) => setRequestedDuration(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl text-xs font-semibold dark:text-white"
                >
                  {requestType === 'extend' ? (
                    <>
                      <option value="14 Tage">14 Tage (+ 2 Wochen Testphase)</option>
                      <option value="30 Tage">30 Tage (+ 1 Monat Testphase)</option>
                      <option value="60 Tage">60 Tage (+ 2 Monate Testphase)</option>
                      <option value="90 Tage">90 Tage (+ 3 Monate Testphase)</option>
                    </>
                  ) : (
                    <>
                      <option value="1 Monat">1 Monat (Monatlich kündbar)</option>
                      <option value="3 Monate">3 Monate Quartalslaufzeit</option>
                      <option value="6 Monate">6 Monate Laufzeit</option>
                      <option value="12 Monate">12 Monate (Jahresabo - empfohlen)</option>
                    </>
                  )}
                </select>
              </div>

              {/* Kontaktdaten */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300">Ihr Name / Ansprechpartner:</label>
                  <input
                    type="text"
                    required
                    placeholder="Vor- und Nachname"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl text-xs font-semibold dark:text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300">E-Mail für Rückfragen:</label>
                  <input
                    type="email"
                    required
                    placeholder="ihre.email@organisation.de"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl text-xs font-semibold dark:text-white"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">Telefonnummer (optional):</label>
                <input
                  type="tel"
                  placeholder="+49 ..."
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl text-xs font-semibold dark:text-white"
                />
              </div>

              {/* Freitext / Begründung */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                  Nachricht / Anmerkung an den Support:
                </label>
                <textarea
                  rows={3}
                  placeholder="z.B. Wir benötigen noch etwas mehr Zeit zur Erprobung der Fahrzeugchecks, oder: Bitte freischalten auf Rechnung..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl text-xs dark:text-white"
                />
              </div>

              {/* Aktionen */}
              <div className="flex justify-end gap-2 pt-3 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-xs font-bold rounded-xl hover:bg-gray-200 dark:bg-gray-700 transition"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-lg transition flex items-center gap-1.5"
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      <span>Wird übermittelt...</span>
                    </>
                  ) : (
                    <>
                      <span>✉️</span>
                      <span>Anfrage als Ticket beim Vertrieb einreichen</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  // --- FALL 0: GLOBALE PORTAL-SPERRE WEGEN ABGELAUFENEM TESTZEITRAUM ---
  if (isPortalLocked && portalLicense) {
    const portalEndDate = portalLicense.portal_trial_end_date ? new Date(portalLicense.portal_trial_end_date) : new Date();
    const portalFakeModule: TrialModule = {
      module_code: 'PORTAL',
      module_name: 'OrgaPilot Portal / Gesamtsystem',
      trial_end_date: portalLicense.portal_trial_end_date || '',
      auto_convert_to_paid: false
    };

    return (
      <div className="min-h-[75vh] flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border-2 border-red-400 dark:border-red-800 overflow-hidden text-center p-8 sm:p-12 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 text-5xl shadow-inner mx-auto ring-8 ring-red-50 dark:ring-red-900/30">
            🏛️
          </div>

          <div className="space-y-2">
            <span className="px-3.5 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs font-black uppercase tracking-widest rounded-full">
              Portal-Testzeitraum Beendet – Zugriff Gesperrt
            </span>
            <h1 className="text-2xl sm:text-4xl font-black text-gray-900 dark:text-whitetext-white tracking-tight">
              Testzeitraum für das OrgaPilot Portal ist abgelaufen
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Der kostenlose Testzeitraum für das OrgaPilot Portal Ihrer Organisation <strong className="text-gray-900 dark:text-whitetext-white font-bold">{portalLicense.organization_name || portalLicense.organization_short_code || ''}</strong> endete am <strong className="text-red-600 dark:text-red-400 font-bold">{portalEndDate.toLocaleDateString('de-DE')}</strong>.
              Gemäß den Systemeinstellungen wurde der Portal-Zugriff vorübergehend gesperrt.
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 text-left max-w-xl mx-auto space-y-3">
            <h4 className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
              Möglichkeiten zur Weiternutzung:
            </h4>
            <div className="space-y-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
              <div className="flex items-start gap-2.5">
                <span className="text-base">⏱️</span>
                <div>
                  <strong className="font-bold">Portal-Testphase verlängern:</strong> Sie können beim Vertrieb eine formlose Verlängerung des Portal-Testzeitraums anfragen.
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-base">💳</span>
                <div>
                  <strong className="font-bold">Portal-Abonnement aktivieren:</strong> Bestellen Sie das reguläre Abonnement, um ohne Unterbrechung auf alle Funktionen zuzugreifen.
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <button
              onClick={() => openRequestModal(portalFakeModule, 'extend')}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-lg hover:shadow-xl transition transform active:scale-95 flex items-center justify-center gap-2"
            >
              <span>⏱️</span>
              <span>Portal-Testphase verlängern anfragen</span>
            </button>

            <button
              onClick={() => openRequestModal(portalFakeModule, 'buy')}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-lg hover:shadow-xl transition transform active:scale-95 flex items-center justify-center gap-2"
            >
              <span>💳</span>
              <span>Portal aktivieren / Kauf anfragen</span>
            </button>
          </div>

          <p className="text-[11px] text-gray-400 dark:text-gray-500 dark:text-gray-400 italic">
            Ihre Anfrage geht direkt als priorisiertes Support-Ticket beim Vertrieb ein.
          </p>

          {renderSupportModal()}
        </div>
      </div>
    );
  }

  // --- FALL 1: MODUL IST ABGELAUFEN & GESPERRT (BLOCKING LOCKOUT SCREEN) ---
  if (isLockedOut) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border-2 border-red-300 dark:border-red-800/80 overflow-hidden text-center p-8 sm:p-12 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 text-5xl shadow-inner mx-auto ring-8 ring-red-50 dark:ring-red-900/30">
            🛑
          </div>

          <div className="space-y-2">
            <span className="px-3.5 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs font-black uppercase tracking-widest rounded-full">
              Testzeitraum Beendet – Zugriff Gesperrt
            </span>
            <h1 className="text-2xl sm:text-4xl font-black text-gray-900 dark:text-whitetext-white tracking-tight">
              Testzeitraum für „{activeModuleTrial.module_name}“ ist abgelaufen
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Der kostenlose Testzeitraum für das Modul <strong className="text-gray-900 dark:text-whitetext-white font-bold">{activeModuleTrial.module_name} ({activeModuleTrial.module_code})</strong> endete am <strong className="text-red-600 dark:text-red-400 font-bold">{endDate.toLocaleDateString('de-DE')}</strong>.
              Gemäß Lizenzierungseinstellung wurde das Modul nach Ablauf deaktiviert und der Zugriff gesperrt.
            </p>
          </div>

          {/* Infobox Weiternutzung */}
          <div className="bg-gray-50 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 text-left max-w-xl mx-auto space-y-3">
            <h4 className="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
              Möglichkeiten zur Weiternutzung:
            </h4>
            <div className="space-y-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
              <div className="flex items-start gap-2.5">
                <span className="text-base">⏱️</span>
                <div>
                  <strong className="font-bold">Testphase verlängern:</strong> Sie können beim Vertrieb eine formlose Verlängerung des Testzeitraums anfragen.
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-base">💳</span>
                <div>
                  <strong className="font-bold">Modul kostenpflichtig aktivieren:</strong> Bestellen Sie das Modul als reguläres Abonnement, um ohne Unterbrechung weiterzuarbeiten.
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <button
              onClick={() => openRequestModal(activeModuleTrial, 'extend')}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-lg hover:shadow-xl transition transform active:scale-95 flex items-center justify-center gap-2"
            >
              <span>⏱️</span>
              <span>Testphase verlängern anfragen</span>
            </button>

            <button
              onClick={() => openRequestModal(activeModuleTrial, 'buy')}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-lg hover:shadow-xl transition transform active:scale-95 flex items-center justify-center gap-2"
            >
              <span>💳</span>
              <span>Modul kaufen / Freischaltung beantragen</span>
            </button>
          </div>

          <p className="text-[11px] text-gray-400 dark:text-gray-500 dark:text-gray-400 italic">
            Ihre Anfrage geht direkt als priorisiertes Support-Ticket beim Vertrieb ein.
          </p>

          {renderSupportModal()}
        </div>
      </div>
    );
  }

  // --- FALL 2: MODUL IST IM TESTZEITRAUM (BANNER OBEN + INHALT DARUNTER) ---
  return (
    <>
      <div className="px-3 sm:px-4 lg:px-6 py-3">
        <div className="space-y-2">
          {currentModuleTrials.map((module) => {
            const modEndDate = new Date(module.trial_end_date);
            const modTimeLeft = modEndDate.getTime() - now.getTime();
            const daysLeft = Math.ceil(modTimeLeft / (24 * 60 * 60 * 1000));
            const isExpiringSoon = modTimeLeft < threeDays && modTimeLeft > 0;
            const modIsExpired = modTimeLeft < 0;

            let bgColor = 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
            let textColor = 'text-blue-800 dark:text-blue-200';
            let iconColor = 'text-blue-600 dark:text-blue-400';
            
            if (modIsExpired) {
              bgColor = 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
              textColor = 'text-red-800 dark:text-red-200';
              iconColor = 'text-red-600 dark:text-red-400';
            } else if (isExpiringSoon) {
              bgColor = 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
              textColor = 'text-orange-800 dark:text-orange-200';
              iconColor = 'text-orange-600 dark:text-orange-400';
            }

            return (
              <div
                key={module.module_code}
                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border ${bgColor} ${textColor} shadow-sm`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`text-2xl ${iconColor} flex-shrink-0 mt-0.5`}>
                    {modIsExpired ? '⚠️' : '🎯'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold text-sm">{module.module_name}</span>
                      <span className="text-xs opacity-75 font-mono">({module.module_code})</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-white dark:bg-gray-800/70 dark:bg-black/30">
                        Testzeitraum
                      </span>
                    </div>
                    <div className="text-xs sm:text-sm mt-1">
                      {modIsExpired ? (
                        <span className="font-medium">
                          Testzeitraum abgelaufen am {modEndDate.toLocaleDateString('de-DE')}
                        </span>
                      ) : (
                        <>
                          <span>Testzeitraum endet am </span>
                          <span className="font-bold">{modEndDate.toLocaleDateString('de-DE')}</span>
                          {daysLeft > 0 && (
                            <span className="ml-1 font-semibold">
                              ({daysLeft} {daysLeft === 1 ? 'Tag' : 'Tage'} verbleibend)
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-xs mt-1 opacity-80 flex items-center gap-2">
                      {module.auto_convert_to_paid ? (
                        <span className="text-emerald-700 dark:text-emerald-300 font-semibold">✓ Wird automatisch in reguläres Abo umgewandelt</span>
                      ) : (
                        <span className="text-amber-700 dark:text-amber-300 font-semibold">⚠️ Wird nach Ablauf deaktiviert & gesperrt</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Action Button to request Extension/Purchase */}
                <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openRequestModal(module, 'extend')}
                    className="px-3 py-1.5 bg-white dark:bg-gray-800/90 dark:bg-gray-800/90 hover:bg-white dark:bg-gray-800hover:bg-gray-800 border border-current rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5"
                  >
                    <span>⏱️</span>
                    <span>Verlängern</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequestModal(module, 'buy')}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5"
                  >
                    <span>💳</span>
                    <span>Modul Kaufen</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {renderSupportModal()}

      {children}
    </>
  );
}
